/**
 * The guided tour — what a first-time visitor is shown, in what order, and how the app remembers
 * that they have seen it.
 *
 * ## Why the script is data
 *
 * Everything a step needs is a string and a selector, so the whole tour is a constant array. That
 * is not tidiness for its own sake: it means the order of the features — the thing a tutorial is
 * actually *for* — can be read, reviewed and tested in one place, without a browser and without
 * the DOM layer that draws it. {@link TourOverlay} knows how to spotlight a rectangle and place a
 * card; it knows nothing about landscapes.
 *
 * ## A step points at the screen, and is dropped if that thing is not on it
 *
 * Every anchored step names one or more selectors, tried in order. The app is built in shapes:
 * a standalone build has no account control, a deployment without localization has no site bar,
 * a build with no terrain editor has no ground tools. A tour that spotlights an empty corner and
 * explains a button that is not there is worse than a shorter tour, so {@link resolveTour} filters
 * the script against what is actually mounted, and the step numbering follows.
 *
 * ## The order is the order of a first session
 *
 * Get a site and say where it is; find out what you are allowed to do; get something to plant and
 * look it up properly; place it, undo it, shape the ground under it, look around it, set the light
 * on it; read the planting back as a schedule, then as labels in the view, then through an
 * ecological question; ask the model for help; photograph it and render it. Features that are
 * genuinely optional (the account) come last, before the closing card.
 *
 * Two steps in that run are deliberately *not* merged even though they share a bar. Manifest and
 * Pointers are the same numbers read two ways — once as a document and once written over the
 * garden — and someone who has only been shown the bar tends to find the button they clicked and
 * never the other one. Same for the camera and the render: one composes a frame, the other spends
 * money on it, and a card that covers both leaves the reader unsure which of the two just happened.
 */

/** Which side of its anchor a card would rather sit on. Flipped when it will not fit. */
export type TourSide = 'top' | 'bottom' | 'left' | 'right' | 'center';

export interface TourStep {
  /** Stable id — used by tests, and by anything that wants to jump into the tour at a step. */
  readonly id: string;
  readonly title: string;
  /** Two or three sentences. Longer than that and it stops being read. */
  readonly body: string;
  /**
   * Selectors tried in order; the first one present anchors the step. An empty list means the card
   * is centred and points at nothing — the opening and closing cards.
   */
  readonly anchors?: readonly string[];
  readonly side?: TourSide;
  /** Shortcuts worth naming, rendered as key caps under the body. */
  readonly keys?: readonly { readonly caps: readonly string[]; readonly text: string }[];
}

/**
 * Bumped when the script changes enough that someone who has already taken the tour should be
 * offered it again. Not bumped for wording fixes — re-interrupting a returning user to re-read a
 * paragraph they have already read is exactly the behaviour that teaches people to dismiss things
 * unread.
 */
export const TOUR_VERSION = 2;

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Lush Glade',
    body:
      'This is a 3D landscape design tool: build a site, plant it, and watch it grow through the ' +
      'seasons. Here is a short pass over the main features — you can leave at any point and pick ' +
      'it up again from the ⓘ button.',
    side: 'center',
  },
  {
    id: 'site',
    title: 'Start with a site',
    body:
      'New site opens on a template, a blank canvas of your own dimensions, or a real place ' +
      'rebuilt from an aerial photograph. Work is saved to a named site and autosaved as you go, ' +
      'so Save is here for the moments you want to be sure rather than for every change.',
    anchors: ['.lg-toolbar__group[aria-label="Project"]'],
    side: 'bottom',
    keys: [{ caps: ['Ctrl', 'S'], text: 'Save' }],
  },
  {
    id: 'localize',
    title: 'Say where the site is',
    body:
      'Localize with a ZIP code or a point on the map, and the whole catalog is read against that ' +
      'place — hardiness, bloom dates, native range, wildlife calendars. Until you do, plants are ' +
      'shown against a reference climate and no suitability is claimed either way.',
    anchors: ['.lg-panel--site'],
    side: 'right',
  },
  {
    id: 'license',
    title: 'Your tier and your key',
    body:
      'Lush Glade is free to design in and paid to render. Standalone, you paste your own OpenAI ' +
      'key here and it stays in this browser. Signed in, renders come out of the allowance on the ' +
      'account and the browser never holds a key at all.',
    anchors: ['.lg-panel--license'],
    side: 'right',
  },
  {
    id: 'palette',
    title: 'The palettes',
    body:
      'Three stacked palettes down the left: plants, wildlife, and objects like benches and ' +
      'boulders. Search and filter the catalog, and open the ⓘ on any tile for its record card — ' +
      'growth curves, seasonal colour, and a live turntable of the plant at maturity.',
    anchors: ['.lg-hud__leftdock'],
    side: 'right',
  },
  {
    id: 'database',
    title: 'The catalog in full',
    body:
      'Database opens the catalog as a place you go rather than a list you scroll: one query read ' +
      'as a gallery, a table, a year, a fit against this site, a wildlife calendar, or a ' +
      'side-by-side comparison. Every figure comes from the model the scene itself uses.',
    anchors: ['.lg-panel__database'],
    side: 'right',
  },
  {
    id: 'tools',
    title: 'Place and edit',
    body:
      'Pick a tile, then click the ground to plant. Move, rotate and maturity act on whatever is ' +
      'selected — maturity grows the plant through its real growth model rather than scaling it. ' +
      'The inspector on the right reports the botanical facts of anything you select.',
    anchors: ['.lg-toolbar__group[aria-label="Tools"]'],
    side: 'bottom',
    keys: [
      { caps: ['P'], text: 'Plant' },
      { caps: ['G'], text: 'Move' },
      { caps: ['R'], text: 'Rotate' },
      { caps: ['M'], text: 'Maturity' },
    ],
  },
  {
    id: 'history',
    title: 'Nothing is permanent',
    body:
      'Every action is undoable, including a whole brush stroke or a wand spell. The history panel ' +
      'shows the run of operations as a list you can step back through.',
    anchors: ['.lg-toolbar__group[aria-label="History"]'],
    side: 'bottom',
    keys: [
      { caps: ['Ctrl', 'Z'], text: 'Undo' },
      { caps: ['Ctrl', 'Shift', 'Z'], text: 'Redo' },
    ],
  },
  {
    id: 'ground',
    title: 'Shape the ground',
    body:
      'Paint lays down grass, mulch, gravel, blacktop and water; sculpt raises, lowers, smooths ' +
      'and flattens. Hold Alt to invert a brush and Shift to smooth, and size it with Ctrl and the ' +
      'wheel. Plants standing on ground you move come with it.',
    anchors: ['.lg-toolbar__group[aria-label="Ground"]'],
    side: 'bottom',
    keys: [
      { caps: ['B'], text: 'Paint' },
      { caps: ['T'], text: 'Sculpt' },
    ],
  },
  {
    id: 'camera',
    title: 'Move around',
    body:
      'Navigation follows SketchUp: middle-drag to orbit, Shift and middle-drag to pan, wheel to ' +
      'zoom toward the cursor. The three view buttons switch between perspective, a top-down plan, ' +
      'and a low presentation angle; the contour map reads the site as a surveyor’s chart.',
    anchors: ['.lg-toolbar__group[aria-label="View"]'],
    side: 'bottom',
    keys: [
      { caps: ['1'], text: 'Perspective' },
      { caps: ['2'], text: 'Top' },
      { caps: ['3'], text: 'Presentation' },
      { caps: ['F'], text: 'Focus selection' },
    ],
  },
  {
    id: 'chrono',
    title: 'Time, season and light',
    body:
      'Drag the date to run the garden through the year — foliage, bloom and bare branches ' +
      'interpolate continuously rather than snapping between four seasons. The hour sets the sun, ' +
      'and the environment tray on the right holds weather and sky.',
    anchors: ['.lg-chrono'],
    side: 'bottom',
  },
  {
    id: 'manifest',
    title: 'The planting as a document',
    body:
      'Manifest states the site as a schedule: one line per species with the nursery container ' +
      'size, spacing, and canopy cover now versus grown, plus every specimen listed and numbered. ' +
      'The plan map draws the same figures in top-down metres. Both export to CSV.',
    // The button, not the panel root: `.lg-manifest` is a positioned container that also holds the
    // full-height sheet and the plan map, so its box is most of the screen and spotlighting it
    // would light everything and point at nothing.
    anchors: ['.lg-manifest__open'],
    side: 'bottom',
  },
  {
    id: 'pointers',
    title: 'Label what is in the view',
    body:
      'Pointers writes each plant and structure’s manifest number over it in the scene, so the ' +
      'schedule and the view are reading the same garden. The menu beside it sets how much a label ' +
      'says, and can limit them to the selection — the way to read one bed in a crowded site.',
    anchors: ['.lg-manifest__pointers'],
    side: 'bottom',
    keys: [{ caps: ['Shift', 'N'], text: 'Pointers' }],
  },
  {
    id: 'lenses',
    title: 'Read the planting by question',
    body:
      'The bar along the bottom reads the design through one ecological question — bird food, ' +
      'pollinator food, caterpillar nursery and more — as a heat overlay drawn where the plants ' +
      'actually are. It follows the date, so a seasonal gap shows up as a place.',
    anchors: ['.lg-sitelens'],
    side: 'top',
  },
  {
    id: 'wand',
    title: 'The wand, and what things are',
    body:
      'The wand takes a point and a sentence — "make this bed drought-tolerant" — and returns an ' +
      'edit you can undo in one step. Tag marks what a thing actually is, so a render stops ' +
      'inventing it; a tag can carry reference photographs as well as words.',
    anchors: ['.lg-toolbar__group[aria-label="Wand"]'],
    side: 'bottom',
    keys: [
      { caps: ['W'], text: 'Wand' },
      { caps: ['Shift', 'T'], text: 'Tag' },
    ],
  },
  {
    id: 'capture',
    title: 'Photograph the site',
    body:
      'Photo mode gives a HUD-free viewfinder with a letterbox showing exactly what the shot will ' +
      'contain, composition guides, and a shutter. Shots are kept with their camera and their ' +
      'weather, so a frame can be retaken after the planting changes.',
    anchors: ['.lg-toolbar__group[aria-label="Capture"]'],
    side: 'bottom',
    keys: [{ caps: ['C'], text: 'Photo mode' }],
  },
  {
    id: 'render',
    title: 'AI render',
    body:
      'Render turns the view into a photograph, an architectural visualisation, or a watercolour. ' +
      'Not from a screenshot alone: a species map, a depth pass and a written manifest go with it, ' +
      'so the model is told which silhouette is which plant. The prompt and cost are shown first.',
    anchors: ['.lg-quick__bar'],
    side: 'bottom',
  },
  {
    id: 'account',
    title: 'Your account',
    body:
      'Signing in moves your sites and shots out of this browser and into your account, so a ' +
      'design opens on any machine. Work made before signing in stays where it is — you are asked ' +
      'whether to copy it across.',
    anchors: ['.lg-account'],
    side: 'left',
  },
  {
    id: 'finish',
    title: 'That is the tour',
    body:
      'Start by placing a few plants and running the date through a year — that is the shortest ' +
      'path to seeing what this is for. The ⓘ button at the top right has the full keyboard ' +
      'reference, and will bring this tour back whenever you want it.',
    side: 'center',
  },
];

/**
 * Drop the steps whose anchor is not on screen, keeping the script's order.
 *
 * `present` is asked rather than the DOM queried directly so this stays testable in node — the
 * overlay passes a predicate that checks both that the element exists and that it has a box, since
 * a control mounted into an empty slot (the account control on a build without accounts) is present
 * in the tree and invisible.
 */
export function resolveTour(
  steps: readonly TourStep[],
  present: (selector: string) => boolean,
): TourStep[] {
  return steps.filter((step) => {
    const anchors = step.anchors ?? [];
    return anchors.length === 0 || anchors.some(present);
  });
}

/** The anchor a step will actually use, or null for a centred card. */
export function anchorFor(step: TourStep, present: (selector: string) => boolean): string | null {
  return (step.anchors ?? []).find(present) ?? null;
}

/* ------------------------------------ Where the card goes ------------------------------------ */

export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface PlacementOptions {
  /** The thing being pointed at, in viewport coordinates. Null centres the card. */
  readonly anchor: Rect | null;
  readonly card: { readonly width: number; readonly height: number };
  readonly viewport: { readonly width: number; readonly height: number };
  readonly side?: TourSide;
  /** Distance from the anchor's edge to the card. */
  readonly gap?: number;
  /** Closest the card is allowed to come to the edge of the window. */
  readonly margin?: number;
}

export interface Placement {
  readonly left: number;
  readonly top: number;
  /** The side actually used, after any flip — the arrow is drawn from this. */
  readonly side: TourSide;
  /**
   * Where the arrow sits along the card's edge, in pixels from its left (for top/bottom) or from
   * its top (for left/right). Null when the card is centred, or when clamping pushed the card so
   * far that the arrow would leave its own edge.
   */
  readonly arrow: number | null;
}

export const TOUR_GAP = 14;
export const TOUR_MARGIN = 16;

/**
 * Put the card beside its anchor: preferred side if it fits, the opposite side if it does not,
 * clamped into the window either way.
 *
 * Flip before clamp, and clamp unconditionally. A card that has been flipped can still overflow
 * along the other axis — a toolbar group at the right-hand end of the bar wants a card below it,
 * which fits vertically and runs off the screen horizontally — so the cross-axis is always pulled
 * back inside, and the arrow is moved along the card's edge to keep pointing at the anchor rather
 * than the card being left pointing at nothing.
 */
export function placeCard(options: PlacementOptions): Placement {
  const { anchor, card, viewport } = options;
  const gap = options.gap ?? TOUR_GAP;
  const margin = options.margin ?? TOUR_MARGIN;

  if (anchor === null || options.side === 'center') {
    return {
      left: Math.round((viewport.width - card.width) / 2),
      top: Math.round((viewport.height - card.height) / 2),
      side: 'center',
      arrow: null,
    };
  }

  const side = flipSide(options.side ?? 'bottom', anchor, card, viewport, gap, margin);
  const anchorCenterX = anchor.left + anchor.width / 2;
  const anchorCenterY = anchor.top + anchor.height / 2;

  let left: number;
  let top: number;
  if (side === 'top' || side === 'bottom') {
    left = anchorCenterX - card.width / 2;
    top = side === 'bottom' ? anchor.top + anchor.height + gap : anchor.top - card.height - gap;
  } else {
    left = side === 'right' ? anchor.left + anchor.width + gap : anchor.left - card.width - gap;
    top = anchorCenterY - card.height / 2;
  }

  left = clamp(left, margin, viewport.width - card.width - margin);
  top = clamp(top, margin, viewport.height - card.height - margin);

  const arrow =
    side === 'top' || side === 'bottom'
      ? arrowOffset(anchorCenterX - left, card.width)
      : arrowOffset(anchorCenterY - top, card.height);

  return { left: Math.round(left), top: Math.round(top), side, arrow };
}

/** The opposite side, when the preferred one has no room. Falls back to the preference. */
function flipSide(
  side: TourSide,
  anchor: Rect,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
  gap: number,
  margin: number,
): TourSide {
  const room = {
    top: anchor.top,
    bottom: viewport.height - (anchor.top + anchor.height),
    left: anchor.left,
    right: viewport.width - (anchor.left + anchor.width),
  };
  const needed = side === 'top' || side === 'bottom' ? card.height : card.width;
  if (side === 'center') return side;
  if (room[side] >= needed + gap + margin) return side;

  const opposite = ({ top: 'bottom', bottom: 'top', left: 'right', right: 'left' } as const)[side];
  // Only flip if the other side is genuinely better; otherwise stay where the script asked and let
  // the clamp do what it can. Flipping into an equally impossible side just moves the problem.
  return room[opposite] > room[side] ? opposite : side;
}

/**
 * Keep the arrow on the card's own edge, or drop it.
 *
 * Two different situations, and they want different answers. An anchor still *over* the card but
 * near its end — a toolbar group at the right-hand end of the bar, whose card has been clamped
 * inward — keeps its arrow, pulled back to the last straight pixel: it points a few pixels off and
 * reads correctly. An anchor whose centre has fallen off the card entirely gets no arrow at all,
 * because one pinned to a rounded corner and aiming at nothing reads as a rendering fault, and the
 * spotlight is already saying where to look.
 */
function arrowOffset(offset: number, length: number): number | null {
  const inset = 20;
  if (offset < 0 || offset > length) return null;
  return Math.round(clamp(offset, inset, length - inset));
}

/** The lit rectangle: the anchor, padded, and never spilling out of the window. */
export function spotlightRect(anchor: Rect, padding: number, viewport: Rect): Rect {
  const left = Math.max(viewport.left, anchor.left - padding);
  const top = Math.max(viewport.top, anchor.top - padding);
  const right = Math.min(viewport.left + viewport.width, anchor.left + anchor.width + padding);
  const bottom = Math.min(viewport.top + viewport.height, anchor.top + anchor.height + padding);
  return { left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

function clamp(value: number, low: number, high: number): number {
  // `high` can be below `low` on a window narrower than the card, and the low bound wins: better to
  // overflow the right edge, which is scrollable nowhere but readable, than the left, which cuts
  // off the start of every line.
  return Math.max(low, Math.min(value, Math.max(low, high)));
}

/* --------------------------------- Who has already seen it --------------------------------- */

/**
 * Whose tour state this is.
 *
 * Keyed by account rather than by browser, because "first time you log in" is a fact about the
 * person, not the machine: a designer who signs in on a second computer is still a first-time
 * visitor there, and two people sharing a workstation each get their own opening. Anonymous use
 * shares one key, which is the best that can be said about a browser nobody has claimed.
 */
export type TourAudience = string;

export const ANONYMOUS_AUDIENCE: TourAudience = 'local';

export function audienceFor(userId: string | null | undefined): TourAudience {
  return userId ? `user:${userId}` : ANONYMOUS_AUDIENCE;
}

export interface TourMemory {
  /** Audience key → the {@link TOUR_VERSION} they last finished or dismissed. */
  readonly seen: Readonly<Record<TourAudience, number>>;
}

export const TOUR_STORAGE_KEY = 'landscape-glade.tour';

export function emptyTourMemory(): TourMemory {
  return { seen: {} };
}

/**
 * Whether this audience should be shown the tour unasked.
 *
 * Offered when they have never seen it, or saw an older version. Dismissing counts the same as
 * finishing: someone who closed it at step two has told us they do not want it, and re-raising it
 * on their next visit is the single most reliable way to make a tutorial resented.
 */
export function shouldOfferTour(
  memory: TourMemory,
  audience: TourAudience,
  version: number = TOUR_VERSION,
): boolean {
  const seen = memory.seen[audience];
  return seen === undefined || seen < version;
}

/** Record that this audience has seen it, however they left. Returns a new memory. */
export function markTourSeen(
  memory: TourMemory,
  audience: TourAudience,
  version: number = TOUR_VERSION,
): TourMemory {
  return { seen: { ...memory.seen, [audience]: version } };
}

/** Parse stored memory, tolerating anything — a corrupt key must not cost anyone the editor. */
export function parseTourMemory(raw: string | null | undefined): TourMemory {
  if (!raw) return emptyTourMemory();
  try {
    const parsed: unknown = JSON.parse(raw);
    const seen = (parsed as { seen?: unknown }).seen;
    if (seen === null || typeof seen !== 'object') return emptyTourMemory();
    const clean: Record<string, number> = {};
    for (const [key, value] of Object.entries(seen as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value)) clean[key] = value;
    }
    return { seen: clean };
  } catch {
    return emptyTourMemory();
  }
}

/** Read the memory from `localStorage`. Storage failures degrade to "nobody has seen it". */
export function readTourMemory(): TourMemory {
  try {
    return parseTourMemory(globalThis.localStorage?.getItem(TOUR_STORAGE_KEY));
  } catch {
    return emptyTourMemory();
  }
}

/** Write it back. Non-fatal: a private window simply gets the tour again next time. */
export function writeTourMemory(memory: TourMemory): void {
  try {
    globalThis.localStorage?.setItem(TOUR_STORAGE_KEY, JSON.stringify(memory));
  } catch {
    // Preferences do not persist this session. Nothing else depends on this succeeding.
  }
}

/** "Step 3 of 11" — announced to screen readers on every advance. */
export function stepProgress(index: number, total: number): string {
  return `Step ${String(index + 1)} of ${String(total)}`;
}

/**
 * The most dots that fit on one line in the card's footer, beside Skip, Back and Next.
 *
 * Measured rather than guessed: a dot and its gap are 9px and the active one widens to 14, against
 * roughly 130px of footer left over once the buttons have taken theirs.
 */
export const TOUR_DOTS_MAX = 12;

/**
 * How to draw "where am I" for a tour of this length.
 *
 * Dots are the better indicator while they fit on one line: they show how many steps are left as a
 * quantity you can take in without reading. Past that they wrap into two ragged rows and stop being
 * legible at a glance, which is worse than the bar — and the exact count is on the card anyway, in
 * words, from {@link stepProgress}.
 */
export function progressStyle(total: number): 'dots' | 'bar' {
  return total > TOUR_DOTS_MAX ? 'bar' : 'dots';
}
